import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { AdminThemeDto } from '@api/types/themes'
import type { AdminUserDto } from '@api/types/admin'
import { themesApi } from '@api/endpoints/themes'
import { adminApi } from '@api/endpoints/admin'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useDebouncedValue } from '@hooks/useDebouncedValue'

interface ThemeAwardDialogProps {
  open: boolean
  theme: AdminThemeDto | null
  onClose: () => void
  onComplete: () => void
}

type Mode = 'award' | 'revoke'

/** A chosen recipient: picked from search (has a name) or typed/pasted as a raw id. */
interface Recipient {
  id: string
  label: string
}

/** Ids pasted as a list, separated by commas, whitespace or newlines. */
const splitIds = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean)

const userLabel = (u: AdminUserDto) => `${u.displayName || u.userName || u.id} (${u.email})`

export function ThemeAwardDialog({ open, theme, onClose, onComplete }: ThemeAwardDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [inputText, setInputText] = useState('')
  const [runningMode, setRunningMode] = useState<Mode | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const debounced = useDebouncedValue(inputText.trim(), 300)
  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'theme-award-picker', debounced],
    queryFn: () => adminApi.getUsers({ search: debounced, pageSize: 10, page: 1 }),
    // Two letters before searching: one letter matches most of the user table.
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  })
  const options = usersQuery.data?.items ?? []

  const dedupe = (list: Recipient[]) => {
    const seen = new Set<string>()
    return list.filter((r) => {
      if (seen.has(r.id)) return false
      seen.add(r.id)
      return true
    })
  }
  const addRecipients = (next: Recipient[]) => setRecipients((current) => dedupe([...current, ...next]))

  // The mode is an argument, not state: the Revoke button used to set state and then submit in
  // the same click, so the submit read the previous mode and awarded instead of revoking.
  const handleSubmit = async (mode: Mode) => {
    if (!theme) return
    const userIds = recipients.map((r) => r.id)
    if (userIds.length === 0) {
      setError('Pick or paste at least one user')
      return
    }

    setRunningMode(mode)
    setError(null)
    setResult(null)
    try {
      if (mode === 'award') {
        const res = await themesApi.awardTheme(theme.id, { userIds })
        setResult(`Awarded theme to ${res.awarded} user(s)`)
      } else {
        const res = await themesApi.revokeTheme(theme.id, { userIds })
        setResult(`Revoked theme from ${res.revoked} user(s)`)
      }
      onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode} theme`)
    } finally {
      setRunningMode(null)
    }
  }

  const handleClose = () => {
    setRecipients([])
    setInputText('')
    setError(null)
    setResult(null)
    onClose()
  }

  const loading = runningMode !== null

  return (
    <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle sx={{ overflowWrap: 'anywhere' }}>Award or revoke: {theme?.name}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && <Alert severity="success" sx={{ mb: 2 }}>{result}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search by name or email, or paste user IDs (commas, spaces or one per line).
        </Typography>
        <Autocomplete<AdminUserDto | Recipient | string, true, false, true>
          multiple
          freeSolo
          filterSelectedOptions
          options={options}
          // The server already filtered by the search text.
          filterOptions={(x) => x}
          loading={usersQuery.isFetching}
          value={recipients}
          inputValue={inputText}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setInputText(value)
          }}
          isOptionEqualToValue={(o, v) =>
            typeof o !== 'string' && typeof v !== 'string' && o.id === v.id
          }
          getOptionLabel={(o) => (typeof o === 'string' ? o : 'email' in o ? userLabel(o) : o.label)}
          onChange={(_, values) => {
            const next: Recipient[] = []
            for (const v of values) {
              if (typeof v === 'string') next.push(...splitIds(v).map((id) => ({ id, label: id })))
              else if ('email' in v) next.push({ id: v.id, label: userLabel(v) })
              else next.push(v)
            }
            setRecipients(dedupe(next))
            setInputText('')
          }}
          renderTags={(values, getTagProps) =>
            values.map((v, index) => {
              const label = typeof v === 'string' ? v : 'email' in v ? userLabel(v) : v.label
              const { key, ...tagProps } = getTagProps({ index })
              return <Chip key={key} size="small" label={label} {...tagProps} sx={{ maxWidth: '100%' }} />
            })
          }
          renderOption={(props, o) => {
            const { key, ...rest } = props as typeof props & { key: string }
            if (typeof o === 'string' || !('email' in o)) return null
            return (
              <Box component="li" key={key} {...rest}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {o.displayName || o.userName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {o.email}
                  </Typography>
                </Box>
              </Box>
            )
          }}
          noOptionsText={
            usersQuery.isError
              ? 'User search failed. You can still paste IDs.'
              : debounced.length < 2
                ? 'Type at least 2 characters'
                : 'No users match'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Users"
              placeholder={recipients.length ? '' : 'Name, email or user ID'}
              // A pasted list becomes one chip per id instead of one chip holding the whole list.
              onPaste={(e) => {
                const ids = splitIds(e.clipboardData.getData('text'))
                if (ids.length > 1) {
                  e.preventDefault()
                  addRecipients(ids.map((id) => ({ id, label: id })))
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersQuery.isFetching && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={() => setConfirmRevoke(true)}
          color="warning"
          variant="outlined"
          disabled={loading || recipients.length === 0}
        >
          {runningMode === 'revoke' ? 'Revoking...' : 'Revoke'}
        </Button>
        <Button
          onClick={() => handleSubmit('award')}
          color="primary"
          variant="contained"
          disabled={loading || recipients.length === 0}
        >
          {runningMode === 'award' ? 'Awarding...' : 'Award'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmRevoke}
        title="Revoke theme?"
        message={`${recipients.length} user(s) will lose "${theme?.name ?? ''}".`}
        confirmLabel="Revoke"
        destructive
        onConfirm={() => {
          setConfirmRevoke(false)
          handleSubmit('revoke')
        }}
        onClose={() => setConfirmRevoke(false)}
      />
    </Dialog>
  )
}
